import type { PDFDocumentProxy, PDFPageProxy } from "pdfjs-dist";

/**
 * Envoltorio fino sobre pdf.js.
 *
 * Un PDF no es texto con formato: es una lista de instrucciones de dibujo. Por
 * eso de cada página se sacan DOS cosas distintas, en paralelo:
 *
 *  - la IMAGEN (`renderPage`), que es lo que se ve;
 *  - la CAPA DE TEXTO (`textLayer`), spans transparentes calzados encima de la
 *    imagen, que es lo que se selecciona. Seleccionás HTML invisible, no la
 *    imagen — el mismo truco que usa el visor de Chrome.
 *
 * pdf.js se carga con `import()` dinámico: son unos cuantos cientos de KB que
 * no tienen por qué pesarle a quien nunca abre un PDF.
 */

type PdfJs = typeof import("pdfjs-dist");

let pdfjsPromise: Promise<PdfJs> | null = null;

async function loadPdfJs(): Promise<PdfJs> {
  if (!pdfjsPromise) {
    pdfjsPromise = import("pdfjs-dist").then((lib) => {
      // El worker se resuelve como asset de Vite; sin esto pdf.js intenta
      // buscarlo por una ruta relativa que en producción no existe.
      lib.GlobalWorkerOptions.workerSrc = new URL(
        "pdfjs-dist/build/pdf.worker.mjs",
        import.meta.url,
      ).href;
      return lib;
    });
  }
  return pdfjsPromise;
}

/** Proporción de la primera página, para dimensionar la tarjeta al soltarla. */
export async function pdfCoverSize(data: Blob): Promise<{ width: number; height: number }> {
  const doc = await PdfDocument.open(data);
  try {
    return await doc.pageSize(0);
  } finally {
    doc.destroy();
  }
}

export interface RenderedPage {
  canvas: HTMLCanvasElement;
  /** Tamaño en px CSS a escala 1 — lo que mide la página en pantalla. */
  cssWidth: number;
  cssHeight: number;
}

/** Un PDF abierto. Cachea las páginas ya pedidas a pdf.js. */
export class PdfDocument {
  private pages = new Map<number, Promise<PDFPageProxy>>();

  private constructor(private doc: PDFDocumentProxy) {}

  static async open(data: Blob): Promise<PdfDocument> {
    const pdfjs = await loadPdfJs();
    // pdf.js se queda con el buffer, así que se le pasa una copia propia.
    const bytes = new Uint8Array(await data.arrayBuffer());
    const doc = await pdfjs.getDocument({ data: bytes }).promise;
    return new PdfDocument(doc);
  }

  get pageCount(): number {
    return this.doc.numPages;
  }

  /** `index` es base 0; pdf.js numera desde 1. */
  private page(index: number): Promise<PDFPageProxy> {
    let p = this.pages.get(index);
    if (!p) {
      p = this.doc.getPage(index + 1);
      this.pages.set(index, p);
    }
    return p;
  }

  /** Tamaño de una página en px CSS a escala 1. */
  async pageSize(index: number): Promise<{ width: number; height: number }> {
    const viewport = (await this.page(index)).getViewport({ scale: 1 });
    return { width: viewport.width, height: viewport.height };
  }

  /**
   * Dibuja una página en un canvas nuevo.
   *
   * `scale` es la resolución: 1 da el tamaño natural, 2 lo dibuja al doble de
   * texels para que se vea nítido en pantallas densas o con zoom.
   */
  async renderPage(index: number, scale: number): Promise<RenderedPage> {
    const page = await this.page(index);
    const viewport = page.getViewport({ scale });
    const base = page.getViewport({ scale: 1 });

    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.floor(viewport.width));
    canvas.height = Math.max(1, Math.floor(viewport.height));

    await page.render({ canvas, viewport }).promise;
    return { canvas, cssWidth: base.width, cssHeight: base.height };
  }

  /**
   * Rellena `container` con la capa de texto invisible de la página.
   *
   * pdf.js posiciona los spans en unidades de página SIN escalar y deja que el
   * CSS los agrande vía `--total-scale-factor`. Por eso acá el viewport va
   * siempre a escala 1: quien muestra la página decide el tamaño moviendo esa
   * variable, y un cambio de ancho de ventana no obliga a rehacer la capa.
   *
   * En un PDF escaneado esto queda vacío: no hay texto que seleccionar, sólo
   * una foto. Ese es el caso que cubre el resaltado de área.
   */
  async textLayer(index: number, container: HTMLElement): Promise<void> {
    const pdfjs = await loadPdfJs();
    const page = await this.page(index);
    const layer = new pdfjs.TextLayer({
      textContentSource: await page.getTextContent(),
      container,
      viewport: page.getViewport({ scale: 1 }),
    });
    await layer.render();
  }

  destroy(): void {
    this.pages.clear();
    // El worker cuelga de la tarea de carga, no del documento: cerrarla es lo
    // que efectivamente libera memoria y termina el hilo.
    void this.doc.loadingTask.destroy();
  }
}
